import React, { useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const BusinessInsight = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Backend API URL configuration
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Using environment variable for API URL
      const response = await axios.post(`${API_URL}/api/business-insight`, {
        input: input,
        timestamp: new Date().toISOString(),
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
        {/* Header and Form Section */}
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent mb-6 py-2 leading-normal">
               💡 Strategic Business Intelligence
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Transform your business vision into actionable strategies with AI-powered insights
            </p>
          </div>

          {/* Main Form Card */}
          <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-3xl shadow-2xl p-8 mb-12 backdrop-blur-sm">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <label htmlFor="business" className="block text-lg font-semibold text-yellow-400 mb-4">
                  Describe your business concept, product, or service
                </label>
                <textarea
                  id="business"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="e.g., Offering an AI-driven M&A deal sourcing SaaS, $10,000–$30,000/month. Location: Global/Online. Features: Intelligent matchmaking between buyers and sellers, private deal listings, industry filtering. Target: investment banks, private equity firms, corporate strategy teams."
                  rows="6"
                  className="w-full px-6 py-4 bg-black/50 border-2 border-yellow-500/30 rounded-2xl text-white placeholder-gray-400 focus:ring-4 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all duration-300 resize-none text-lg backdrop-blur-sm"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className={`group relative w-full py-5 px-8 rounded-2xl font-bold text-lg transition-all duration-300 transform focus:outline-none overflow-hidden ${
                  loading || !input.trim()
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-500 text-black hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-400 hover:scale-[1.02] hover:shadow-2xl hover:shadow-yellow-500/30 active:scale-[0.98]'
                }`}
              >
                {!loading && !input.trim() && (
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/0 via-yellow-400/10 to-yellow-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
                )}
                {loading || !input.trim() || (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-300"></div>
                )}
                <span className="relative z-10">
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-3 border-yellow-400 border-t-transparent rounded-full animate-spin mr-3"></div>
                      Generating Strategic Insights...
                    </div>
                  ) : (
                    'Generate Business Intelligence'
                  )}
                </span>
              </button>
            </form>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-gradient-to-r from-red-900/20 to-red-800/20 border border-red-500/30 p-6 mb-8 rounded-2xl animate-slide-up backdrop-blur-sm">
              <div className="flex items-center">
                <div className="text-red-400 mr-4 text-2xl">⚠️</div>
                <div>
                  <h3 className="text-red-300 font-semibold text-lg">Analysis Error</h3>
                  <p className="text-red-200">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Section - Outside the constrained container */}
        {result && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
            <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-3xl shadow-2xl p-8 animate-slide-up backdrop-blur-sm">
              <h2 className="text-3xl font-bold text-yellow-400 mb-8 flex items-center">
                Strategic Business Intelligence Report
              </h2>
              <div className="bg-black/40 rounded-2xl p-8 border border-yellow-500/10">
                {typeof result === 'string' ? (
                  <div className="prose prose-lg prose-invert max-w-none text-gray-200">
                    <ReactMarkdown>
                      {result.startsWith('```markdown\n') && result.endsWith('\n```') 
                        ? result.slice(11, -4)
                        : result
                      }
                    </ReactMarkdown>
                  </div>
                ) : (
                  <pre className="text-gray-200 whitespace-pre-wrap text-lg leading-relaxed font-mono">
                    {JSON.stringify(result, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessInsight;